import type { Dict } from '../index.js';

/** "Password must be at least {n} characters" for every allowed minimum length (settings: 8–128). */
const passwordLength: Dict = Object.fromEntries(
  Array.from({ length: 128 - 8 + 1 }, (_, i) => [
    `Password must be at least ${i + 8} characters`,
    `يجب ألا تقل كلمة المرور عن ${i + 8} حرفًا`,
  ]),
);

/** Static reasons that can follow "Could not reach the official site: …". */
const officialSiteReasons: Record<string, string> = {
  'Too many redirects': 'عدد كبير جدًا من عمليات إعادة التوجيه',
  'Official page is unexpectedly large': 'حجم الصفحة الرسمية أكبر من المتوقع',
  'fetch failed': 'تعذّر الاتصال',
  'The operation was aborted due to timeout': 'انتهت مهلة الاتصال',
  'Invalid source URL': 'رابط المصدر غير صالح',
  'Source URL must use https': 'يجب أن يستخدم رابط المصدر https',
  'Source URL must be on www.presidency.eg': 'يجب أن يكون رابط المصدر على www.presidency.eg',
};
const officialSite: Dict = Object.fromEntries(
  Object.entries(officialSiteReasons).map(([en, ar]) => [
    `Could not reach the official site: ${en}. You can paste the page text instead.`,
    `تعذّر الوصول إلى الموقع الرسمي: ${ar}. يمكنك لصق نص الصفحة بدلًا من ذلك.`,
  ]),
);

/** Leave errors that can block an approval ("Cannot approve: …"). */
const leaveBlocking: Record<string, string> = {
  'This employee is deactivated.': 'هذا الموظف غير مفعَّل.',
  'A request cannot be longer than one year.': 'لا يمكن أن تتجاوز مدة الطلب سنة واحدة.',
  'Some dates are outside the employment period.': 'بعض التواريخ خارج فترة التوظيف.',
  'There are no working days in the selected dates (weekends and public holidays are not counted).':
    'لا توجد أيام عمل في التواريخ المحددة (لا تُحتسب عطلات نهاية الأسبوع والعطلات الرسمية).',
};
const cannotApprove: Dict = Object.fromEntries(
  Object.entries(leaveBlocking).map(([en, ar]) => [`Cannot approve: ${en}`, `تعذّرت الموافقة: ${ar}`]),
);


/**
 * Texts the server builds with values filled in. Keys with {placeholders} are matched against the
 * finished English sentence by translate() (see ../index.ts); the English must be character-for-character
 * what the server writes around the values.
 *
 * fmtDays() on the server writes "1 day" / "2.5 days", so sentences containing it are listed once per
 * singular/plural form: in the templates below "[[x]]" stands for "{x} day" or "{x} days".
 * A leave date range is either one date or "start → end"; "[[range]]" expands to both forms.
 */
function expand(en: string, ar: string): Dict {
  let out: [string, string][] = [[en, ar]];
  const step = (token: RegExp, forms: (m: RegExpExecArray) => [string, string][]) => {
    const next: [string, string][] = [];
    for (const [e, a] of out) {
      const m = token.exec(e);
      if (!m) {
        next.push([e, a]);
        continue;
      }
      for (const [fe, fa] of forms(m)) next.push([e.replace(m[0], fe), a.replace(m[0], fa)]);
    }
    out = next;
    return next.some(([e]) => token.test(e));
  };
  while (step(/\[\[range\]\]/, () => [['{range}', '{range}'], ['{start} → {end}', '{start} ← {end}']]));
  while (step(/\[\[(\w+)\]\]/, (m) => [[`{${m[1]}} day`, `{${m[1]}} يوم`], [`{${m[1]}} days`, `{${m[1]}} يوم`]]));
  // Keep only forms translate() can use as patterns (enough literal text around the values).
  return Object.fromEntries(out.filter(([e]) => e.replace(/\{\w+\}/g, '').replace(/\s+/g, '').length >= 12));
}
export const expandAll = (list: [string, string][]): Dict => Object.assign({}, ...list.map(([en, ar]) => expand(en, ar)));

/** Leave preview errors that depend on values (modules/leave/service.ts evaluateDraft). */
const leaveDynamicErrors: [string, string][] = [
  ['"{type}" does not apply to employees in this country.', '"{type}" لا تنطبق على الموظفين في هذه الدولة.'],
  ['Half days are not allowed for "{type}".', 'أنصاف الأيام غير مسموح بها في "{type}".'],
  [
    'Leave can be requested at most {n} days in the past. Ask an administrator to record older leave.',
    'يمكن طلب الإجازة بأثر رجعي لمدة أقصاها {n} يوم. اطلب من مسؤول النظام تسجيل الإجازات الأقدم.',
  ],
  ['"{type}" allows at most [[max]] per request.', 'يسمح "{type}" بحد أقصى {max} يوم في الطلب الواحد.'],
  ['You already have leave on {dates}.', 'لديك إجازة بالفعل في {dates}.'],
  [
    'Not enough {type} balance for {year}: [[available]] available, [[requested]] requested.',
    'رصيد {type} غير كافٍ للفترة {year}: المتاح {available} يوم، والمطلوب {requested} يوم.',
  ],
  [
    '"{type}" is limited to [[max]] per leave year ([[total]] including this request).',
    'الحد الأقصى لـ "{type}" هو {max} يوم في سنة الإجازة ({total} يوم مع هذا الطلب).',
  ],
];
const leaveDynamic: Dict = {
  ...expandAll(leaveDynamicErrors),
  '"{type}" is no longer available.': '"{type}" لم يعد متاحًا.',
  // Warnings
  'This takes the {year} balance below zero ({after}).': 'سيصبح رصيد الفترة {year} أقل من الصفر ({after}).',
  '"{type}" does not reduce any balance.': '"{type}" لا يُخصم من أي رصيد.',
};
/** approveInternal re-checks the draft and reports the first error as "Cannot approve: …". */
const cannotApproveDynamic: Dict = expandAll(leaveDynamicErrors.map(([en, ar]) => [`Cannot approve: ${en}`, `تعذّرت الموافقة: ${ar}`]));

/** Notifications about leave (modules/leave/service.ts). Leave type names are translated when known. */
const leaveNotifications: Dict = expandAll([
  ["{type}: [[range]] ([[days]]). You'll be notified when it is reviewed.", '{type}: [[range]] ({days} يوم). ستتلقى إشعارًا عند مراجعة الطلب.'],
  ['{type}: [[range]] ([[days]]) — "{comment}".', '{type}: [[range]] ({days} يوم) — "{comment}".'],
  ['{type}: [[range]] — [[days]] returned to your balance.', '{type}: [[range]] — أُعيد {days} يوم إلى رصيدك.'],
  ['{type}: [[range]] — [[days]] returned to your balance. Reason: {reason}.', '{type}: [[range]] — أُعيد {days} يوم إلى رصيدك. السبب: {reason}.'],
  ['{type} balance increased by [[n]]', 'زيادة رصيد {type} بمقدار {n} يوم'],
  ['{type} balance reduced by [[n]]', 'تخفيض رصيد {type} بمقدار {n} يوم'],
]);

/** Values that appear inside server sentences and are translated on their own. */
const values: Dict = {
  // Payroll note: how computer hours are counted (attendance.computer_hours_count)
  'time the computer was in use, idle excluded': 'وقت استخدام الكمبيوتر، دون وقت الخمول',
  'computer time in use, idle and unproductive excluded': 'وقت استخدام الكمبيوتر، دون وقت الخمول والوقت غير المنتج',
  'productive computer time only': 'وقت الكمبيوتر المنتج فقط',
  'all recorded computer time, idle included': 'كل وقت الكمبيوتر المسجَّل، بما في ذلك وقت الخمول',
};

/** Arabic: server. Keys are the exact English text. */
export const server: Dict = {
  ...passwordLength,
  ...officialSiteReasons,
  ...officialSite,
  ...leaveBlocking,
  ...cannotApprove,
  ...leaveDynamic,
  ...cannotApproveDynamic,
  ...leaveNotifications,
  ...values,

  // ---- General / HTTP ----
  'Please sign in': 'يرجى تسجيل الدخول',
  'You do not have permission to do this': 'ليست لديك صلاحية للقيام بهذا الإجراء',
  'Some fields are invalid': 'بعض الحقول غير صالحة',
  'Missing request header': 'ترويسة الطلب مفقودة',
  'Cross-origin request rejected': 'تم رفض الطلب القادم من مصدر خارجي',
  'Too many requests. Please wait a moment and try again.': 'طلبات كثيرة جدًا. يرجى الانتظار قليلًا ثم المحاولة مرة أخرى.',
  'Request body too large': 'حجم الطلب كبير جدًا',
  'Malformed request': 'طلب غير صالح',
  'Not found': 'غير موجود',
  'Something went wrong. It has been logged.': 'حدث خطأ ما، وتم تسجيله.',
  'Something went wrong': 'حدث خطأ ما',
  'You must change your password before continuing': 'يجب تغيير كلمة المرور قبل المتابعة',
  'Your account is not linked to an employee record': 'حسابك غير مرتبط بسجل موظف',

  // ---- Database constraints ----
  'An employee with this employee ID already exists': 'يوجد موظف آخر بنفس الرقم الوظيفي',
  'An employee with this e-mail already exists': 'يوجد موظف آخر بنفس البريد الإلكتروني',
  'Another employee already has this fingerprint machine ID': 'رقم جهاز البصمة هذا مستخدم لموظف آخر',
  'A device with this name already exists': 'يوجد جهاز آخر بنفس الاسم',
  'There is already a salary record with that effective date': 'يوجد سجل راتب بتاريخ السريان نفسه',
  'A device with this serial number already exists': 'يوجد جهاز آخر بنفس الرقم التسلسلي',
  'A login account with this e-mail already exists': 'يوجد حساب دخول آخر بنفس البريد الإلكتروني',
  'This employee already has a login account': 'لدى هذا الموظف حساب دخول بالفعل',
  'A department with this name already exists': 'يوجد قسم آخر بنفس الاسم',
  'A work schedule with this name already exists': 'يوجد جدول عمل آخر بنفس الاسم',
  'A holiday calendar with this name already exists': 'يوجد تقويم عطلات آخر بنفس الاسم',
  'This holiday already exists on that date': 'هذه العطلة موجودة بالفعل في ذلك التاريخ',
  'There is already a work-terms change on that effective date': 'يوجد تغيير في شروط العمل بتاريخ السريان نفسه',
  'An employee cannot be their own manager': 'لا يمكن أن يكون الموظف مديرًا لنفسه',
  'That manager would create a reporting-line loop': 'اختيار هذا المدير سيُنشئ حلقة في التسلسل الإداري',
  'End date must be on or after the start date': 'يجب أن يكون تاريخ الانتهاء في تاريخ البدء أو بعده',
  'Manager and employee accounts must be linked to an employee record': 'يجب ربط حسابات المديرين والموظفين بسجل موظف',
  'Invalid time zone': 'منطقة زمنية غير صالحة',
  'This record already exists': 'هذا السجل موجود بالفعل',
  'This record is still in use and cannot be removed. Deactivate it instead.': 'هذا السجل ما زال مستخدمًا ولا يمكن حذفه. قم بإلغاء تفعيله بدلًا من ذلك.',
  'A referenced record does not exist': 'السجل المشار إليه غير موجود',
  'A value is outside the allowed range': 'إحدى القيم خارج النطاق المسموح',
  'A required value is missing': 'إحدى القيم المطلوبة مفقودة',
  'A value has an invalid format': 'إحدى القيم بصيغة غير صالحة',

  // ---- "… not found" ----
  'Punch not found': 'التسجيل غير موجود',
  'Device not found': 'الجهاز غير موجود',
  'Department not found': 'القسم غير موجود',
  'Employee not found': 'الموظف غير موجود',
  'Work terms not found': 'شروط العمل غير موجودة',
  'Payslip not found': 'قسيمة الراتب غير موجودة',
  'Reporting period not found': 'الفترة غير موجودة',
  'Holiday calendar not found': 'تقويم العطلات غير موجود',
  'Holiday not found': 'العطلة غير موجودة',
  'Leave request not found': 'طلب الإجازة غير موجود',
  'Balance not found': 'الرصيد غير موجود',
  'Leave type not found': 'نوع الإجازة غير موجود',
  'Leave policy not found': 'سياسة الإجازات غير موجودة',
  'Notification not found': 'الإشعار غير موجود',
  'Salary record not found': 'سجل الراتب غير موجود',
  'Adjustment not found': 'التعديل غير موجود',
  'Computer not found': 'الكمبيوتر غير موجود',
  'Rule not found': 'القاعدة غير موجودة',
  'Work schedule not found': 'جدول العمل غير موجود',
  'Login account not found': 'حساب الدخول غير موجود',
  'Manager not found': 'المدير غير موجود',

  // ---- Field-level detail messages ----
  Required: 'مطلوب',
  Invalid: 'غير صالح',
  'Not allowed': 'غير مسموح',
  'Too long': 'طويل جدًا',
  'Incorrect password': 'كلمة المرور غير صحيحة',
  'Must differ from current password': 'يجب أن تختلف عن كلمة المرور الحالية',
  'Inactive policy': 'سياسة غير مفعّلة',
  'Inactive schedule': 'جدول غير مفعّل',
  'Inactive calendar': 'تقويم غير مفعّل',
  'Before start date': 'قبل تاريخ البدء',
  'Use the deactivate action': 'استخدم إجراء إلغاء التفعيل',
  'Use the reactivate action': 'استخدم إجراء إعادة التفعيل',
  'Required for past effective dates': 'مطلوب لتواريخ السريان السابقة',
  'Not needed for the same currency': 'غير مطلوب عند استخدام العملة نفسها',
  'Unknown setting': 'إعداد غير معروف',
  'Invalid value': 'قيمة غير صالحة',

  // ---- Sign-in & passwords ----
  'Incorrect e-mail or password, or the account is temporarily locked.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة، أو أن الحساب مقفل مؤقتًا.',
  'Current password is incorrect': 'كلمة المرور الحالية غير صحيحة',
  'New password must be different': 'يجب أن تختلف كلمة المرور الجديدة',
  'Password is too long': 'كلمة المرور طويلة جدًا',
  'Use at least three of: lower-case, upper-case, digits, symbols': 'استخدم ثلاثة على الأقل مما يلي: أحرف صغيرة، أحرف كبيرة، أرقام، رموز',
  'Password must not contain your e-mail name': 'يجب ألا تحتوي كلمة المرور على اسم بريدك الإلكتروني',

  // ---- Attendance ----
  '"to" must be on or after "from"': 'يجب أن يكون تاريخ "إلى" في تاريخ "من" أو بعده',
  '"To" must be on or after "From"': 'يجب أن يكون تاريخ "إلى" في تاريخ "من" أو بعده',
  'The date range is limited to 3 months': 'الحد الأقصى للفترة 3 أشهر',
  'The date range is limited to 93 days': 'الحد الأقصى للفترة 93 يومًا',
  'The date range is limited to 366 days': 'الحد الأقصى للفترة 366 يومًا',
  'The date range is limited to one year': 'الحد الأقصى للفترة سنة واحدة',
  'Fingerprint machines are identified by their serial number and do not use a key.': 'تُعرَّف أجهزة البصمة برقمها التسلسلي ولا تستخدم مفتاحًا.',
  'Choose the device or the time zone of the times in the file': 'اختر الجهاز أو المنطقة الزمنية للأوقات الواردة في الملف',
  'This kiosk is not paired or has been disabled. Ask an administrator for a new pairing link.': 'جهاز الباب هذا غير مربوط أو تم تعطيله. اطلب من مسؤول النظام رابط ربط جديدًا.',
  'Invalid device key': 'مفتاح الجهاز غير صالح',
  'Device network not allowed': 'شبكة الجهاز غير مسموح بها',
  'Your employee record is deactivated.': 'سجلك الوظيفي غير مفعَّل.',
  "Your hours come from your computer's activity — there is no need to clock in.": 'تُحتسب ساعاتك من نشاط جهاز الكمبيوتر — لا حاجة لتسجيل الحضور.',
  'Clocking in from the app is turned off. Use the fingerprint machine or the door QR code.': 'تسجيل الحضور من التطبيق متوقف. استخدم جهاز البصمة أو رمز QR على الباب.',
  'Office staff clock in with the fingerprint machine or the door QR code.': 'يسجّل موظفو المكتب حضورهم عبر جهاز البصمة أو رمز QR على الباب.',
  'This QR code is not valid. Scan the code on the office screen again.': 'رمز QR هذا غير صالح. امسح الرمز الظاهر على شاشة المكتب مرة أخرى.',
  'This QR code has expired. Scan the code on the office screen again.': 'انتهت صلاحية رمز QR هذا. امسح الرمز الظاهر على شاشة المكتب مرة أخرى.',
  'Scanning only works on the office Wi-Fi. Connect to the office network and try again.': 'المسح يعمل فقط عبر شبكة Wi-Fi الخاصة بالمكتب. اتصل بشبكة المكتب ثم حاول مرة أخرى.',
  'The employee has no work terms on that date': 'لا توجد شروط عمل للموظف في ذلك التاريخ',
  'Invalid date or time': 'تاريخ أو وقت غير صالح',
  'A punch cannot be in the future': 'لا يمكن أن يكون التسجيل في المستقبل',
  'This punch is already voided.': 'هذا التسجيل ملغى بالفعل.',
  'This day belongs to a closed reporting period and can no longer be changed. An administrator must reopen that period first.':
    'هذا اليوم ضمن فترة مُقفلة ولم يعد بالإمكان تعديله. يجب على مسؤول النظام إعادة فتح تلك الفترة أولًا.',
  'Unrecognised line': 'سطر غير معروف',
  'Unrecognised record': 'سجل غير معروف',
  'Invalid date/time': 'تاريخ/وقت غير صالح',
  'Time is in the future — check the device clock': 'الوقت في المستقبل — تحقّق من ساعة الجهاز',
  'Could not recognise the file format. Export as CSV with columns for ID and date/time.': 'تعذّر التعرف على صيغة الملف. صدّره بصيغة CSV مع أعمدة للرقم والتاريخ/الوقت.',

  // ---- Departments ----
  'Department head must be an active employee': 'يجب أن يكون رئيس القسم موظفًا مفعَّلًا',
  'Employees (including former employees) are assigned to this department. Deactivate it instead.':
    'هناك موظفون (بمن فيهم موظفون سابقون) مرتبطون بهذا القسم. قم بإلغاء تفعيله بدلًا من ذلك.',

  // ---- Employees & accounts ----
  'Only an administrator with payroll access can reset the password of an account that has payroll access.':
    'لا يمكن إعادة تعيين كلمة مرور حساب لديه صلاحية الرواتب إلا من قِبل مسؤول نظام لديه صلاحية الرواتب.',
  'Leave policy is inactive': 'سياسة الإجازات غير مفعّلة',
  'Manager is deactivated': 'المدير غير مفعَّل',
  'Work schedule is inactive': 'جدول العمل غير مفعّل',
  'Holiday calendar is inactive': 'تقويم العطلات غير مفعّل',
  'New employees cannot be created as terminated': 'لا يمكن إنشاء موظف جديد بحالة منتهية خدمته',
  'Use "Deactivate employee" to terminate an employee': 'استخدم "إلغاء تفعيل الموظف" لإنهاء خدمة موظف',
  'Use "Reactivate employee" to bring back a deactivated employee': 'استخدم "إعادة تفعيل الموظف" لإعادة موظف غير مفعَّل',
  'End date must be after start date': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء',
  'A reason is required for changes that take effect in the past': 'يجب ذكر سبب للتغييرات التي تسري بأثر رجعي',
  'Effective date cannot be before the employment start date': 'لا يمكن أن يكون تاريخ السريان قبل تاريخ بدء التوظيف',
  'Terms that are already in effect cannot be deleted — add a new change instead': 'لا يمكن حذف شروط سارية بالفعل — أضف تغييرًا جديدًا بدلًا من ذلك',
  'Employee is already deactivated': 'الموظف غير مفعَّل بالفعل',
  'End date is before the start date': 'تاريخ الانتهاء قبل تاريخ البدء',
  'You cannot deactivate your own employee record': 'لا يمكنك إلغاء تفعيل سجلك الوظيفي',
  'Employee is already active': 'الموظف مفعَّل بالفعل',
  'This employee has a login account. Deactivate the employee instead of deleting.': 'لدى هذا الموظف حساب دخول. قم بإلغاء تفعيل الموظف بدلًا من حذفه.',
  "Reassign this employee's direct reports before deleting.": 'انقل المرؤوسين المباشرين لهذا الموظف إلى مدير آخر قبل الحذف.',
  'This employee is a department head. Change the department head first.': 'هذا الموظف رئيس قسم. غيّر رئيس القسم أولًا.',
  'This is the last active administrator account and cannot be removed or demoted': 'هذا آخر حساب مسؤول نظام مفعَّل ولا يمكن حذفه أو تخفيض صلاحياته',
  'Cannot create a login for a deactivated employee': 'لا يمكن إنشاء حساب دخول لموظف غير مفعَّل',
  'You cannot demote or disable your own account': 'لا يمكنك تخفيض صلاحيات حسابك أو تعطيله',
  'Reactivate the employee before enabling their login': 'أعد تفعيل الموظف قبل تفعيل حساب الدخول الخاص به',
  'You cannot remove your own payroll access': 'لا يمكنك إزالة صلاحية الرواتب عن نفسك',
  'At least one active administrator must keep payroll access': 'يجب أن يحتفظ مسؤول نظام مفعَّل واحد على الأقل بصلاحية الرواتب',

  // ---- Exports / periods ----
  'Choose a period or dates': 'اختر فترة أو تواريخ',
  'This period is already closed.': 'هذه الفترة مُقفلة بالفعل.',
  'This period is not closed.': 'هذه الفترة غير مُقفلة.',

  // ---- Holidays ----
  'This calendar is not linked to an official source. Edit the calendar and choose a source first.': 'هذا التقويم غير مرتبط بمصدر رسمي. عدّل التقويم واختر مصدرًا أولًا.',
  'Pasted text is too long': 'النص الملصق طويل جدًا',
  'No holidays found': 'لم يتم العثور على عطلات',
  'No holidays were found on the page. The page layout may have changed.': 'لم يتم العثور على عطلات في الصفحة. ربما تغيّر تصميم الصفحة.',
  'This calendar is (or was) assigned to employees and is kept for history. Deactivate it instead.':
    'هذا التقويم مُسند (أو كان مُسندًا) إلى موظفين ويُحتفظ به للسجل. قم بإلغاء تفعيله بدلًا من ذلك.',

  // ---- Leave ----
  'This request is already approved.': 'تمت الموافقة على هذا الطلب بالفعل.',
  'This request is already rejected.': 'تم رفض هذا الطلب بالفعل.',
  'This request is already cancelled.': 'تم إلغاء هذا الطلب بالفعل.',
  'You cannot approve your own leave.': 'لا يمكنك الموافقة على إجازتك.',
  'You are not allowed to approve leave for this employee.': 'غير مسموح لك بالموافقة على إجازات هذا الموظف.',
  'You cannot review your own leave.': 'لا يمكنك مراجعة إجازتك.',
  'You are not allowed to review leave for this employee.': 'غير مسموح لك بمراجعة إجازات هذا الموظف.',
  'Please give a reason for cancelling approved leave': 'يرجى ذكر سبب إلغاء الإجازة الموافق عليها',
  'Date is before the employment start date': 'التاريخ قبل تاريخ بدء التوظيف',
  'Only the employee or an administrator can cancel this request.': 'لا يمكن إلغاء هذا الطلب إلا من قِبل الموظف أو مسؤول النظام.',
  'Approved leave can only be cancelled by an administrator.': 'لا يمكن إلغاء الإجازة الموافق عليها إلا من قِبل مسؤول النظام.',
  'This leave has already started. Ask an administrator to cancel it.': 'بدأت هذه الإجازة بالفعل. اطلب من مسؤول النظام إلغاءها.',
  'This request spans two leave years; days are counted against each year separately.': 'يمتد هذا الطلب على سنتي إجازة؛ تُحتسب الأيام من رصيد كل سنة على حدة.',
  'You cannot approve your own leave. Submit it without "approve now" so another administrator can approve it.':
    'لا يمكنك الموافقة على إجازتك. أرسلها دون "الموافقة الآن" ليوافق عليها مسؤول نظام آخر.',
  'Calendar range is limited to 3 months': 'الحد الأقصى لنطاق التقويم 3 أشهر',
  'This leave type is used by policies, balances or requests. Deactivate it instead.': 'نوع الإجازة هذا مستخدم في سياسات أو أرصدة أو طلبات. قم بإلغاء تفعيله بدلًا من ذلك.',
  'This policy is (or was) assigned to employees and is kept for history. Deactivate it instead.':
    'هذه السياسة مُسندة (أو كانت مُسندة) إلى موظفين ويُحتفظ بها للسجل. قم بإلغاء تفعيلها بدلًا من ذلك.',

  // ---- Payroll & settings ----
  'Salary records already in effect cannot be removed — add a new one instead.': 'لا يمكن حذف سجلات الرواتب السارية بالفعل — أضف سجلًا جديدًا بدلًا من ذلك.',
  'Choose two different currencies': 'اختر عملتين مختلفتين',
  'This payroll is already finalized.': 'تم الاعتماد النهائي لهذه الرواتب بالفعل.',
  'This payroll is not finalized.': 'لم يتم الاعتماد النهائي لهذه الرواتب.',
  'A fixed rate is only needed when the salary and pay currencies differ': 'لا حاجة لسعر صرف ثابت إلا عندما تختلف عملة الراتب عن عملة الدفع',
  'Only an administrator with payroll access can change payroll settings.': 'لا يمكن تغيير إعدادات الرواتب إلا من قِبل مسؤول نظام لديه صلاحية الرواتب.',
  'Some settings are invalid': 'بعض الإعدادات غير صالحة',

  // ---- Productivity / desktop agent ----
  'This employee is deactivated.': 'هذا الموظف غير مفعَّل.',
  'This connection code is not valid or has expired. Ask an administrator for a new one.': 'رمز الربط هذا غير صالح أو منتهي الصلاحية. اطلب رمزًا جديدًا من مسؤول النظام.',
  'Missing device token': 'رمز الجهاز مفقود',
  'This computer is no longer connected. Ask an administrator for a new enrolment code.': 'لم يعد هذا الكمبيوتر مربوطًا. اطلب رمز ربط جديدًا من مسؤول النظام.',
  'This computer is already disconnected.': 'تم فصل هذا الكمبيوتر بالفعل.',
  'The employee has not accepted the current monitoring notice.': 'لم يوافق الموظف على إشعار المراقبة الحالي.',
  'The notice has changed — please read the current version and accept again.': 'تم تغيير الإشعار — يرجى قراءة النسخة الحالية والموافقة عليها مرة أخرى.',

  // ---- Schedules ----
  'Break is longer than the working day': 'الاستراحة أطول من يوم العمل',
  'This schedule is assigned to active employees. Move them to another schedule first.': 'جدول العمل هذا مُسند إلى موظفين مفعَّلين. انقلهم إلى جدول آخر أولًا.',
  'This schedule is (or was) used by employees and is kept for history. Deactivate it instead.':
    'جدول العمل هذا مستخدم (أو كان مستخدمًا) من قِبل موظفين ويُحتفظ به للسجل. قم بإلغاء تفعيله بدلًا من ذلك.',

  // ---- Dynamic server sentences (values filled in by the server) ----
  // Reporting periods (modules/periods)
  'Close {label} first — periods are closed in order.': 'أقفل {label} أولًا — تُقفل الفترات بالترتيب.',
  'The period has not finished yet for: {names}. Try again once their last day is over.': 'لم تنتهِ الفترة بعد بالنسبة إلى: {names}. حاول مرة أخرى بعد انتهاء آخر يوم لهم.',
  'The payroll for {label} is finalized — reopen the payroll first.': 'تم الاعتماد النهائي لرواتب {label} — أعد فتح الرواتب أولًا.',
  'Reopen {label} first — only the most recent closed period can be reopened.': 'أعد فتح {label} أولًا — لا يمكن إعادة فتح إلا آخر فترة مُقفلة.',
  'This day falls in {label}, which is closed. An administrator must reopen that period first.': 'هذا اليوم ضمن {label}، وهي فترة مُقفلة. يجب على مسؤول النظام إعادة فتح تلك الفترة أولًا.',
  'This leave falls in {label}, which is closed. An administrator must reopen that period first.': 'هذه الإجازة ضمن {label}، وهي فترة مُقفلة. يجب على مسؤول النظام إعادة فتح تلك الفترة أولًا.',
  'This change falls in {label}, which is closed. An administrator must reopen that period first.': 'هذا التغيير ضمن {label}، وهي فترة مُقفلة. يجب على مسؤول النظام إعادة فتح تلك الفترة أولًا.',
  'Closed automatically {n} day(s) after the period ended': 'أُقفلت تلقائيًا بعد {n} يوم/أيام من انتهاء الفترة',

  // Payroll (modules/payroll/service.ts)
  'Payroll for {label} is already finalized. Use an effective date after that period, or reopen its payroll first.':
    'تم الاعتماد النهائي لرواتب {label} بالفعل. استخدم تاريخ سريان بعد تلك الفترة، أو أعد فتح رواتبها أولًا.',
  "Couldn't get the {from} → {to} rate online ({error}). Enter it manually.": 'تعذّر جلب سعر الصرف من {from} إلى {to} عبر الإنترنت ({error}). أدخله يدويًا.',
  'The online source has no {from} → {to} rate. Enter it manually.': 'لا يوفّر المصدر الإلكتروني سعر الصرف من {from} إلى {to}. أدخله يدويًا.',
  'The payroll for {label} is finalized. Reopen it first.': 'تم الاعتماد النهائي لرواتب {label}. أعد فتحها أولًا.',
  'Reopen the payroll for {label} first.': 'أعد فتح رواتب {label} أولًا.',
  // Payroll blockers
  "Close the reporting period {label} first (Reports) so the hours can't change.": 'أقفل الفترة {label} أولًا (التقارير) حتى لا تتغيّر الساعات.',
  'Set the {from} → {to} rate for this month.': 'حدّد سعر الصرف من {from} إلى {to} لهذا الشهر.',
  // Payslip line notes
  'No {from} → {to} rate set for this month yet.': 'لم يُحدَّد سعر الصرف من {from} إلى {to} لهذا الشهر بعد.',
  'Public holiday not counted as required: {days}. If the office worked that day, remove it from the holiday calendar.':
    'عطلة رسمية لم تُحتسب ضمن الساعات المطلوبة: {days}. إذا عمل المكتب في ذلك اليوم، فاحذفها من تقويم العطلات.',
  'Public holidays not counted as required: {days}. If the office worked that day, remove it from the holiday calendar.':
    'عطلات رسمية لم تُحتسب ضمن الساعات المطلوبة: {days}. إذا عمل المكتب في ذلك اليوم، فاحذفها من تقويم العطلات.',
  'Does not clock in — hours from the desktop agent ({how}).': 'لا يسجّل الحضور — الساعات من برنامج سطح المكتب ({how}).',
  '{n} day(s) with a missing clock-in/out — hours may be understated.': '{n} يوم/أيام فيها تسجيل حضور أو انصراف ناقص — قد تكون الساعات أقل من الفعلية.',
  'Employed {n} of {total} days — salary prorated.': 'موظَّف {n} من أصل {total} يوم — الراتب محتسب بالتناسب.',

  // Attendance
  'Missing clock-out on {when}': 'تسجيل انصراف ناقص يوم {when}',
  'Missing clock-in on {when}': 'تسجيل حضور ناقص يوم {when}',
  '{name}: missing clock-out on {when}': '{name}: تسجيل انصراف ناقص يوم {when}',
  '{name}: missing clock-in on {when}': '{name}: تسجيل حضور ناقص يوم {when}',
  'Please tell an administrator the correct time so your worked hours are right.': 'يرجى إبلاغ مسؤول النظام بالوقت الصحيح حتى تكون ساعات عملك صحيحة.',
  'Add the correct punch on the Attendance page.': 'أضف التسجيل الصحيح في صفحة الحضور.',
  'Could not find the ID and date/time columns. Found: {headers}': 'تعذّر العثور على عمودي الرقم والتاريخ/الوقت. الأعمدة الموجودة: {headers}',
  'Invalid date/time "{value}"': 'تاريخ/وقت غير صالح "{value}"',
  'Desktop agent · {host}': 'برنامج سطح المكتب · {host}',
  'The date range is limited to {n} days': 'الحد الأقصى للفترة {n} يومًا',

  // Leave notifications (titles)
  'Leave request submitted': 'تم إرسال طلب الإجازة',
  'New leave request from {name}': 'طلب إجازة جديد من {name}',
  'Leave recorded': 'تم تسجيل الإجازة',
  'Leave request approved': 'تمت الموافقة على طلب الإجازة',
  'Leave request rejected': 'تم رفض طلب الإجازة',
  'Leave request cancelled': 'تم إلغاء طلب الإجازة',
  'Your leave was cancelled': 'تم إلغاء إجازتك',
  '{name} cancelled approved leave': 'ألغى {name} إجازة موافقًا عليها',
  '{type} allowance changed to {n} days': 'تم تغيير استحقاق {type} إلى {n} يوم',
  'Previously {n} days. Reason: {reason}': 'سابقًا {n} يوم. السبب: {reason}',

  // Other notifications
  'Welcome! Your account is ready': 'مرحبًا! حسابك جاهز',
  'Please change your temporary password and review your profile.': 'يرجى تغيير كلمة المرور المؤقتة ومراجعة ملفك الشخصي.',
  'Notifications are on': 'تم تفعيل الإشعارات',
  'You will get approvals, reminders and reports on this device.': 'ستصلك الموافقات والتذكيرات والتقارير على هذا الجهاز.',
  'Review missing punches and pending leave, then close the period to freeze it.': 'راجع التسجيلات الناقصة والإجازات المعلّقة، ثم أقفل الفترة لتجميدها.',
  'Automatic backup failed': 'فشل النسخ الاحتياطي التلقائي',
  'The daily database backup could not be made: {error}. Check that PostgreSQL tools (pg_dump) are installed and BACKUP_DIR is writable.':
    'تعذّر إنشاء النسخة الاحتياطية اليومية لقاعدة البيانات: {error}. تحقّق من تثبيت أدوات PostgreSQL ‏(pg_dump) ومن إمكانية الكتابة في BACKUP_DIR.',
  'Productivity report — {label}': 'تقرير الإنتاجية — {label}',
  "Your team's productivity — {label}": 'إنتاجية فريقك — {label}',
  // Daily productivity notice body (one line each; the web translates line by line)
  '{n} person tracked · score {pct}%': 'شخص واحد مسجَّل · النتيجة {pct}%',
  '{n} people tracked · score {pct}%': '{n} أشخاص مسجَّلون · النتيجة {pct}%',
  'Productive {productive} · Unproductive {unproductive} · Idle {idle}': 'منتج {productive} · غير منتج {unproductive} · خامل {idle}',
  'Below target: {names}': 'دون الهدف: {names}',

  // Holidays (official source sync)
  '{name} holidays updated from the official site': 'تم تحديث عطلات {name} من الموقع الرسمي',
  '{name}: official holidays changed': '{name}: تغيّرت العطلات الرسمية',
  'Up to date with the official {year} list ({n} dates).': 'مطابق للقائمة الرسمية لعام {year} ({n} تاريخ).',
  '{added} date(s) to add, {removed} to remove for {year}.': '{added} تاريخ/تواريخ للإضافة و{removed} للحذف لعام {year}.',
  'Up to date with the official {year} list ({n} dates). Review and apply them on the Holidays page.':
    'مطابق للقائمة الرسمية لعام {year} ({n} تاريخ). راجِع التغييرات وطبّقها في صفحة العطلات.',
  '{added} date(s) to add, {removed} to remove for {year}. Review and apply them on the Holidays page.':
    '{added} تاريخ/تواريخ للإضافة و{removed} للحذف لعام {year}. راجِع التغييرات وطبّقها في صفحة العطلات.',
  'Synced with the official {year} list: {added} added, {removed} removed.': 'تمت المزامنة مع القائمة الرسمية لعام {year}: أُضيف {added} وحُذف {removed}.',
  'Could not reach the official site: {reason}. You can paste the page text instead.': 'تعذّر الوصول إلى الموقع الرسمي: {reason}. يمكنك لصق نص الصفحة بدلًا من ذلك.',
  'The official site answered with HTTP {status}': 'ردّ الموقع الرسمي برمز HTTP {status}',
  'Redirect without location ({status})': 'إعادة توجيه دون عنوان ({status})',
  'Source URL must be on {host}': 'يجب أن يكون رابط المصدر على {host}',
  'Page is labelled {labelled}, but the weekdays match {year}; using {year}.': 'الصفحة معنونة بعام {labelled}، لكن أيام الأسبوع تطابق عام {year}؛ سيُستخدم عام {year}.',
  'Skipped invalid date for "{name}".': 'تم تخطي تاريخ غير صالح لـ "{name}".',
  '"{name}": {date} is a {weekday}, but the page says {pageWeekday}. Please double-check.': '"{name}": يوافق {date} يوم {weekday}، لكن الصفحة تذكر {pageWeekday}. يرجى التحقق.',
  '"{name}": unusual date range, only the first day was imported.': '"{name}": نطاق تواريخ غير معتاد، تم استيراد اليوم الأول فقط.',

  // Static server texts shown as notes / reasons
  'The period is still running — figures so far.': 'الفترة ما زالت جارية — الأرقام حتى الآن.',
  'Does not clock in — paid the fixed monthly salary.': 'لا يسجّل الحضور — يُدفع له الراتب الشهري الثابت.',
  'Recorded and approved by administrator': 'سجّلها ووافق عليها مسؤول النظام',
  'No approval required for this leave type': 'لا يحتاج نوع الإجازة هذا إلى موافقة',
  'Yearly entitlement from leave policy': 'الاستحقاق السنوي وفق سياسة الإجازات',
  'No leave policy assigned': 'لم تُسند سياسة إجازات',
  'Unused days carried over from the previous leave year': 'أيام غير مستخدمة مُرحَّلة من سنة الإجازة السابقة',
  'Leave cancelled': 'تم إلغاء الإجازة',
  'Synchronised with official source': 'تمت المزامنة مع المصدر الرسمي',
  'Tracking is turned off by the company.': 'أوقفت الشركة التتبّع.',
  'Waiting for you to accept the notice.': 'بانتظار موافقتك على الإشعار.',
  'You are not clocked in.': 'لم تسجّل حضورك.',
  'Outside your working hours.': 'خارج ساعات عملك.',
  'Backups are turned off (BACKUPS=off) or no database address is set.': 'النسخ الاحتياطي متوقف (BACKUPS=off) أو لم يُحدَّد عنوان قاعدة البيانات.',
  'The backup could not be made. Check that the PostgreSQL tools (pg_dump) are installed on the server and the backup folder is writable.':
    'تعذّر إنشاء النسخة الاحتياطية. تحقّق من تثبيت أدوات PostgreSQL ‏(pg_dump) على الخادم ومن إمكانية الكتابة في مجلد النسخ الاحتياطية.',

  // ---- Web push ----
  'Not a known browser push service': 'ليست خدمة إشعارات متصفح معروفة',

  // ---- Shared validation (zod) ----
  'Please choose an option': 'يرجى اختيار أحد الخيارات',
  'Use YYYY-MM-DD': 'استخدم الصيغة YYYY-MM-DD',
  'Invalid date': 'تاريخ غير صالح',
  'Use HH:MM (24h)': 'استخدم الصيغة HH:MM (24 ساعة)',
  'Choose a valid IANA time zone (e.g. Asia/Beirut). Fixed offsets are not allowed.': 'اختر منطقة زمنية صالحة وفق IANA (مثل Asia/Beirut). الفروق الثابتة غير مسموح بها.',
  'Choose a country': 'اختر دولة',
  'Please give a reason (min 3 characters)': 'يرجى ذكر السبب (3 أحرف على الأقل)',
  'Start and end time cannot be equal': 'لا يمكن أن يتساوى وقت البدء ووقت الانتهاء',
  'Each weekday may appear once': 'يجب ألا يتكرر أي يوم من أيام الأسبوع',
  'Choose a work schedule': 'اختر جدول عمل',
  'Employee ID is required': 'الرقم الوظيفي مطلوب',
  'Max 30 characters': '30 حرفًا كحد أقصى',
  'Letters, digits, dot, dash or underscore only': 'أحرف وأرقام ونقطة وشرطة وشرطة سفلية فقط',
  'Enter the full name': 'أدخل الاسم الكامل',
  'Enter a valid e-mail address': 'أدخل بريدًا إلكترونيًا صالحًا',
  'Letters, digits, dash or underscore only': 'أحرف وأرقام وشرطة وشرطة سفلية فقط',
  'Up to 12 letters/digits': 'حتى 12 حرفًا/رقمًا',
  'Each leave type may appear once': 'يجب ألا يتكرر أي نوع إجازة',
  'For a single half day, choose either morning or afternoon': 'لنصف يوم واحد، اختر إما الصباح أو بعد الظهر',
  'Enter a non-zero number of days': 'أدخل عدد أيام لا يساوي صفرًا',
  'Use whole or half days': 'استخدم أيامًا كاملة أو أنصاف أيام',
  'Letters and digits only': 'أحرف وأرقام فقط',
  'Use an IP address or range, e.g. 41.33.10.20 or 41.33.10.0/24': 'استخدم عنوان IP أو نطاقًا، مثل 41.33.10.20 أو 41.33.10.0/24',
  'Use a 3-letter currency code, e.g. EGP': 'استخدم رمز عملة من 3 أحرف، مثل EGP',
  'Enter the monthly salary': 'أدخل الراتب الشهري',
  'Enter a non-zero amount': 'أدخل مبلغًا لا يساوي صفرًا',
  'Too large': 'كبير جدًا',
  'Say what it is for': 'اذكر الغرض منه',
  'Choose a department or one person, not both': 'اختر قسمًا أو شخصًا واحدًا، وليس كليهما',
  'Enter a website like youtube.com': 'أدخل موقعًا مثل youtube.com',
  'Enter a name': 'أدخل اسمًا',
  'Must be a valid IANA time zone, e.g. Asia/Beirut': 'يجب أن تكون منطقة زمنية صالحة وفق IANA، مثل Asia/Beirut',
  'The image is too large (max 500 KB)': 'الصورة كبيرة جدًا (الحد الأقصى 500 KB)',
  'Must be an https:// address': 'يجب أن يكون عنوانًا يبدأ بـ https://',
  // Common zod defaults
  'Invalid input': 'إدخال غير صالح',
  'Invalid email': 'بريد إلكتروني غير صالح',
  'Invalid url': 'رابط غير صالح',
  'Invalid uuid': 'معرّف غير صالح',
};
